import { useState, useEffect } from "react";
import { useDriversApi } from "@/hooks/useDriversApi";
import { useBranches } from "@/hooks/useBranches";
import { useBrandsApi } from "@/hooks/useBrandsApi";
import OrdersTable from "@/components/orders/OrdersTable";
import DriverModal from "@/components/modals/DriverModal";
import { Driver, Branch } from "@/types/supabase";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Plus, Search, XCircle } from "lucide-react";
import { Input } from "@/components/atoms/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/atoms/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/atoms/tabs";
import { Card } from "@/components/atoms/card";
import Pagination from "@/components/atoms/Pagination";
import { useDebounce } from "@/hooks/useDebounce";
import { EmptyState } from "@/components/atoms/empty-state";
import { toastHandler } from "@/lib/ToastHandler";
import { Button } from "@/components/atoms/button";
import { usePermissions } from "@/contexts/PermissionContext";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/hooks/useAuth";

// Extended Driver interface with statistics
interface DriverWithStats extends Driver {
  total_orders?: number;
  total_completed_orders?: number;
  total_activity_time_minutes?: number;
}

export default function DriversList() {
  const [tab, setTab] = useState<"activos" | "inactivos">("activos");
  const [statusFilter, setStatusFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [search, setSearch] = useState("");
  // AGREGADO: Estado local para búsqueda de sucursales
  const [branchSearch, setBranchSearch] = useState("");
  const debouncedBranchSearch = useDebounce(branchSearch, 200);
  const debouncedSearch = useDebounce(search, 500); // 500ms debounce
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [drivers, setDrivers] = useState<DriverWithStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchDriversPaginated, updateDriver } = useDriversApi();
  const { branches, loading: loadingBranches, fetchBranches } = useBranches();
  const { brands, fetchAllBrands } = useBrandsApi();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { isManager, user } = useAuth();
  // Fetch brands only if not already loaded
  useEffect(() => {
    if (brands.length === 0 && fetchAllBrands) {
      fetchAllBrands(isManager ? user : null);
    }
    if (branches.length === 0 && fetchBranches) {
      fetchBranches();
    }
  }, []);

  // Build filters for API (helper function)
  const getFilters = () => {
    const filters: any = {};
    if (tab === "activos") filters.active = true;
    if (tab === "inactivos") filters.active = false;
    if (statusFilter && statusFilter !== "all")
      filters.vehicle_type = statusFilter;
    if (branchFilter && branchFilter !== "all")
      filters.branch_id = branchFilter;
    if (debouncedSearch) filters.search = debouncedSearch;
    return filters;
  };

  useEffect(() => {
    // Reset to page 1 when filters or debouncedSearch change
    setCurrentPage(1);
  }, [tab, statusFilter, branchFilter, debouncedSearch]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Fetch drivers with all filters and pagination
  const fetchDrivers = async () => {
    setLoading(true);
    setError(null); // Clear any previous errors

    try {
      // Si el search cambió, forzar page=1
      const pageToFetch = debouncedSearch ? 1 : currentPage;
      const response = await fetchDriversPaginated({
        page: pageToFetch,
        limit: PAGE_SIZE,
        ...getFilters(),
      });

      if (Array.isArray(response)) {
        setDrivers(response);
        setError(null); // Clear error on successful response
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response
      ) {
        setDrivers(response.data);
        setTotalCount(response.total || 0);
        setError(null); // Clear error on successful response
      } else {
        setDrivers([]);
        setError(null); // Clear error even if no data
      }
    } catch (err: any) {
      console.error("Error fetching drivers:", err);

      // Handle different types of errors
      if (err?.response?.status === 500) {
        setError(
          "Error interno del servidor. Por favor, inténtalo nuevamente en unos momentos. Si el problema persiste, contacta al administrador.",
        );
      } else if (err?.response?.status === 404) {
        setError("No se pudo encontrar el recurso solicitado.");
      } else if (err?.response?.status === 403) {
        setError("No tienes permisos para acceder a esta información.");
      } else if (err?.response?.status === 401) {
        setError("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
      } else if (
        err?.code === "NETWORK_ERROR" ||
        err?.message?.includes("Network Error")
      ) {
        setError(
          "Error de conexión. Verifica tu conexión a internet e inténtalo nuevamente.",
        );
      } else if (err?.response?.data?.message) {
        setError(`Error del servidor: ${err.response.data.message}`);
      } else if (err?.message) {
        setError(`Error: ${err.message}`);
      } else {
        setError(
          "Ha ocurrido un error inesperado al cargar los conductores. Por favor, inténtalo nuevamente.",
        );
      }

      // Don't clear drivers on error, keep the previous state if any
      // setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [tab, statusFilter, branchFilter, debouncedSearch, currentPage]);

  // Show toast notification for critical errors
  useEffect(() => {
    if (
      error &&
      (error.includes("Error interno del servidor") ||
        error.includes("Error de conexión"))
    ) {
      toastHandler({
        title: "Problema de conectividad",
        description:
          "Hay un problema temporal con el servidor. Los datos se actualizarán automáticamente cuando se resuelva.",
        variant: "destructive",
      });
    }
  }, [error]);

  const handleUpdateDriverStatus = async (
    driverId: string,
    active: boolean,
  ) => {
    setLoading(true);
    try {
      await updateDriver(Number(driverId), { active: !active });
      await fetchDrivers();
      toastHandler({
        description: !active
          ? "Conductor activado correctamente."
          : "Conductor desactivado correctamente.",
        variant: "success",
      });
    } catch (err: any) {
      console.error("Error updating driver status:", err);
      toastHandler({
        title: "Error al actualizar conductor",
        description:
          err?.response?.data?.message ||
          err?.message ||
          "Ha ocurrido un error inesperado.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format minutes to hours and minutes
  const formatActivityTime = (minutes: number | null | undefined): string => {
    if (!minutes || minutes === 0) return "0h 0m";

    // Round to nearest minute first
    const roundedMinutes = Math.round(minutes);

    // Calculate hours and remaining minutes
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = roundedMinutes % 60;

    return `${hours}h ${remainingMinutes}m`;
  };

  // AGREGADO: Filtrado de sucursales en base a búsqueda (case insensitive)
  const filteredBranches =
    debouncedBranchSearch && debouncedBranchSearch.trim().length > 0
      ? branches.filter((branch: Branch) =>
          branch.name
            .toLowerCase()
            .includes(debouncedBranchSearch.trim().toLowerCase()),
        )
      : branches;

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      {/* Header */}
      <PageHeader
        title="Conductores"
        description="Conductores disponibles y rendimiento"
      />

      {/* Tabs */}
      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as "activos" | "inactivos")}
      >
        <TabsList className="bg-white w-full justify-start pl-6 gap-4">
          <TabsTrigger value="activos">Activos</TabsTrigger>
          <TabsTrigger value="inactivos">Inactivos</TabsTrigger>
        </TabsList>
        <TabsContent
          value={tab}
          className="lg:px-4 md:px-6 md:py-1 max-w-7xl mx-auto mt-0"
        >
          <Card className="flex flex-col gap-6 p-6 bg-white rounded-none md:rounded-xl md:mt-4 ">
            {/* Actions */}
            <div className="flex flex-col gap-6">
              {/* Search and Filters Section */}
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                {/* Left side - Search and Filters */}
                <div className="flex flex-col gap-4 w-full lg:flex-row lg:items-center lg:flex-1">
                  {/* Search Input */}
                  <div className="flex-1 min-w-0">
                    <Input
                      className="w-full text-sm"
                      placeholder="Buscar conductor..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      icon={
                        <Search
                          className="w-4 h-4 text-gray-400"
                          strokeWidth={2}
                        />
                      }
                    />
                  </div>
                  {/* Vehicle Filter */}
                  <div className="w-full lg:w-48 flex-1">
                    <Select
                      value={statusFilter}
                      onValueChange={setStatusFilter}
                    >
                      <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="Tipo de vehículo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los vehículos</SelectItem>
                        <SelectItem value="moto">Motocicleta</SelectItem>
                        <SelectItem value="bicycle">Bicicleta</SelectItem>
                        <SelectItem value="car">Automóvil</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Branch Filter Mejorado con buscador */}
                  <div className="w-full lg:w-56 flex-1">
                    <Select
                      value={branchFilter}
                      onValueChange={setBranchFilter}
                      disabled={loadingBranches}
                    >
                      <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="Sucursal" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        {/* Buscador de sucursales */}
                        <div className="sticky top-0 z-10 bg-white px-2 pt-2 pb-1">
                          <Input
                            placeholder="Buscar sucursal..."
                            className="w-full text-xs"
                            value={branchSearch}
                            onChange={(e) => setBranchSearch(e.target.value)}
                            // icon (opcional, por si se quiere una lupa)
                            icon={
                              <Search
                                className="w-3 h-3 text-gray-400"
                                strokeWidth={2}
                              />
                            }
                          />
                        </div>
                        <SelectItem value="all">
                          Todas las sucursales
                        </SelectItem>
                        {(filteredBranches.length > 0
                          ? filteredBranches
                          : []
                        ).map((branch: Branch) => (
                          <SelectItem key={branch.id} value={String(branch.id)}>
                            {branch.name}
                          </SelectItem>
                        ))}
                        {filteredBranches.length === 0 && (
                          <div className="py-2 px-3 text-xs text-gray-500 select-none">
                            No se encontraron sucursales
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>{" "}
                  <div className="w-full lg:w-auto flex-1">
                    {hasPermission("driver:create") && (
                      <DriverModal
                        trigger={
                          <Button className="w-full lg:w-auto px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2 shadow-sm">
                            <Plus className="w-4 h-4" strokeWidth={2} />
                            Nuevo conductor
                          </Button>
                        }
                        refreshDrivers={async () => {
                          await fetchDrivers();
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Right side - Action Button */}
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div className="p-6 text-center text-gray-600">
                Cargando conductores...
              </div>
            ) : error ? (
              <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Error al cargar conductores
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{error}</p>
                      </div>
                      <div className="mt-4">
                        <button
                          onClick={() => fetchDrivers()}
                          className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                        >
                          Intentar nuevamente
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : drivers.length === 0 ? (
              search === "" &&
              branchFilter === "all" &&
              statusFilter === "all" ? (
                <EmptyState
                  title="No hay conductores registrados"
                  description="Aún no se ha registrado ningún conductor en el sistema."
                  illustration={{
                    src: "/driver-illustration/empty-driver.svg",
                    alt: "Sin conductores",
                  }}
                />
              ) : (
                <EmptyState
                  title="No se encontraron conductores"
                  description="No hay conductores que coincidan con los filtros o la búsqueda actual."
                  illustration={{
                    src: "/driver-illustration/empty-driver.svg",
                    alt: "Sin conductores",
                  }}
                />
              )
            ) : (
              <>
                {/* Desktop Table */}

                <div className="hidden xl:block">
                  <OrdersTable
                    columns={[
                      { header: "Nombre", key: "name" },
                      { header: "Vehículo", key: "vehicle" },
                      { header: "Órdenes Totales", key: "total_orders" },
                      {
                        header: "Órdenes Completadas",
                        key: "completed_orders",
                      },
                      { header: "Última conexión", key: "last_time_connected" },
                      { header: "Contrato", key: "contract" },
                      { header: "Estado", key: "status" },
                      { header: "Tiempo Activo", key: "activity_time" },
                      { header: "Acciones", key: "actions" },
                    ]}
                    children={drivers.map((driver) => (
                      <tr
                        key={driver.id}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/drivers/${driver.id}`)}
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold">
                              <img
                                className="w-8 h-8 rounded-full object-cover"
                                src={
                                  driver.picture ||
                                  "/driver-illustration/profile-pic-default.svg"
                                }
                                alt={`Avatar de ${driver.name}`}
                              />
                            </div>
                            <span className="font-medium text-gray-900 text-nowrap">
                              {driver.name || "Sin nombre"}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {driver.vehicle_type === "moto" ||
                          driver.vehicle_type === "motorcycle"
                            ? "Moto"
                            : driver.vehicle_type === "bicycle"
                              ? "Bicicleta"
                              : driver.vehicle_type === "car"
                                ? "Automóvil"
                                : "N/A"}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {driver.total_orders || 0}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {driver.total_completed_orders || 0}
                        </td>
                        <td className="py-4 px-4 text-gray-700 text-nowrap">
                          {driver.last_time_connected
                            ? (() => {
                                // Convertir a horario de El Salvador (America/El_Salvador)
                                const fecha = new Date(
                                  driver.last_time_connected,
                                );
                                // Formatear fecha y hora en español, zona horaria El Salvador
                                const opcionesFecha = {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  timeZone: "America/El_Salvador",
                                } as const;
                                const opcionesHora = {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "America/El_Salvador",
                                } as const;
                                const fechaFormateada =
                                  fecha.toLocaleDateString(
                                    "es-SV",
                                    opcionesFecha,
                                  );
                                const horaFormateada = fecha.toLocaleTimeString(
                                  "es-SV",
                                  opcionesHora,
                                );
                                return `${fechaFormateada} - ${horaFormateada}`;
                              })()
                            : "-"}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {driver.worker_type === "full_time"
                            ? "Full-time"
                            : driver.worker_type === "part_time"
                              ? "Part-time"
                              : driver.worker_type
                                ? driver.worker_type.charAt(0).toUpperCase() +
                                  driver.worker_type.slice(1)
                                : "N/A"}
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className={`text-green-600 text-nowrap font-medium rounded-lg text-sm px-2 py-1 ${
                              !driver.driver_working && "bg-red-50 text-red-500"
                            }`}
                          >
                            {driver.driver_working
                              ? "Disponible"
                              : "No disponible"}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-gray-700 text-nowrap">
                          {formatActivityTime(
                            driver.total_activity_time_minutes,
                          )}
                        </td>
                        {hasPermission("driver:update") &&
                        hasPermission("driver:deactivate") &&
                        driver.id !== 19 ? (
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              {hasPermission("driver:update") && (
                                <DriverModal
                                  trigger={
                                    <button
                                      className="inline-flex items-center px-2 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                    >
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                        />
                                      </svg>
                                    </button>
                                  }
                                  driverToEdit={driver}
                                  refreshDrivers={async () => {
                                    fetchDrivers();
                                  }}
                                />
                              )}

                              {hasPermission("driver:deactivate") && (
                                <button
                                  className={`text-sm text-gray-600 p-2 rounded-lg hover:bg-gray-100 hover:text-gray-600`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateDriverStatus(
                                      String(driver.id),
                                      driver.active ?? false,
                                    );
                                  }}
                                >
                                  {driver.active ? (
                                    <XCircle className="w-4 h-4" />
                                  ) : (
                                    <CheckCircle2 className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        ) : (
                          <td className="py-4 px-4 text-center">-</td>
                        )}
                      </tr>
                    ))}
                    emptyMessage="No hay conductores asignados"
                  />
                </div>

                <div className="xl:hidden space-y-3 xs:space-y-4">
                  {drivers.map((driver) => (
                    <div
                      key={driver.id}
                      className="bg-white rounded-xl border border-gray-200 p-3 xs:p-4 sm:p-5 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                      onClick={() => navigate(`/drivers/${driver.id}`)}
                    >
                      {/* Header with avatar and actions */}
                      <div className="flex items-start justify-between mb-3 xs:mb-4">
                        <div className="flex items-center space-x-2 xs:space-x-3 min-w-0 flex-1">
                          <div className="w-8 h-8 xs:w-10 xs:h-10 sm:w-12 sm:h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                            <img
                              className="w-8 h-8 xs:w-10 xs:h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                              src={
                                driver.picture ||
                                "/driver-illustration/profile-pic-default.svg"
                              }
                              alt={`Avatar de ${driver.name}`}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-gray-900 text-xs xs:text-sm sm:text-base truncate">
                              {driver.name || "Sin nombre"}
                            </h3>
                            <p className="text-xs text-gray-600 truncate">
                              {driver.vehicle_type === "moto" ||
                              driver.vehicle_type === "motorcycle"
                                ? "🏍️ Motocicleta"
                                : driver.vehicle_type === "bicycle"
                                  ? "🚲 Bicicleta"
                                  : driver.vehicle_type === "car"
                                    ? "🚗 Automóvil"
                                    : "🚛 Vehículo"}
                            </p>
                          </div>
                        </div>

                        {/* Status badge */}
                        <div className="flex items-center space-x-1 xs:space-x-2 flex-shrink-0">
                          <span
                            className={`inline-flex items-center px-1.5 py-1.5 xs:px-2 sm:px-2.5  xs:py-1 rounded-full text-xs font-medium ${
                              driver.driver_working
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            <div
                              className={`w-1 h-1 xs:w-1.5 xs:h-1.5 rounded-full sm:mr-1.5 ${
                                driver.driver_working
                                  ? "bg-green-500"
                                  : "bg-red-500"
                              }`}
                            />
                            <span className="hidden xs:inline sm:hidden">
                              {driver.driver_working
                                ? "Disponible"
                                : "No disponible"}
                            </span>
                            <span className="hidden sm:inline">
                              {driver.driver_working ? "Disp." : "No disp."}
                            </span>
                          </span>
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 xs:gap-3 sm:gap-4 mb-3 xs:mb-4">
                        <div className="bg-blue-50 rounded-lg flex flex-col items-center sm:items-start p-1.5 xs:p-2 sm:p-3">
                          <div className="text-base xs:text-lg sm:text-2xl font-bold text-blue-600">
                            {driver.total_orders || 0}
                          </div>
                          <div className="text-xs text-blue-600 font-medium">
                            Órdenes Totales
                          </div>
                        </div>
                        <div className="bg-green-50 rounded-lg flex flex-col items-center sm:items-start p-1.5 xs:p-2 sm:p-3">
                          <div className="text-base xs:text-lg sm:text-2xl font-bold text-green-600">
                            {driver.total_completed_orders || 0}
                          </div>
                          <div className="text-xs text-green-600 font-medium">
                            Completadas
                          </div>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="space-y-1.5 xs:space-y-2 sm:space-y-3 text-xs sm:text-sm">
                        <div className="flex items-center justify-between py-0.5 xs:py-1">
                          <span className="text-gray-600 font-medium">
                            Tiempo Activo:
                          </span>
                          <span className="text-gray-900 font-semibold text-right">
                            {formatActivityTime(
                              driver.total_activity_time_minutes,
                            )}
                          </span>
                        </div>
                        <div className="flex items-center justify-between py-0.5 xs:py-1">
                          <span className="text-gray-600 font-medium">
                            Contrato:
                          </span>
                          <span className="text-gray-900 truncate ml-1 xs:ml-2 text-right">
                            {driver.worker_type === "full_time"
                              ? "Full-time"
                              : driver.worker_type === "part_time"
                                ? "Part-time"
                                : driver.worker_type
                                  ? driver.worker_type.charAt(0).toUpperCase() +
                                    driver.worker_type.slice(1)
                                  : "N/A"}
                          </span>
                        </div>
                        <div className="flex items-start py-0.5 xs:py-1">
                          <span className="text-gray-600 font-medium mr-1 whitespace-nowrap">
                            Última conexión:
                          </span>
                          <span className="text-gray-900 text-right sm:text-left break-words flex-1 min-w-0">
                            {driver.last_time_connected
                              ? (() => {
                                  // Convertir a horario de El Salvador (America/El_Salvador)
                                  const fecha = new Date(
                                    driver.last_time_connected,
                                  );
                                  // Formatear fecha y hora en español, zona horaria El Salvador
                                  const opcionesFecha = {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    timeZone: "America/El_Salvador",
                                  } as const;
                                  const opcionesHora = {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                    timeZone: "America/El_Salvador",
                                  } as const;
                                  const fechaFormateada =
                                    fecha.toLocaleDateString(
                                      "es-SV",
                                      opcionesFecha,
                                    );
                                  const horaFormateada =
                                    fecha.toLocaleTimeString(
                                      "es-SV",
                                      opcionesHora,
                                    );
                                  return `${fechaFormateada} - ${horaFormateada}`;
                                })()
                              : "-"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between py-0.5 xs:py-1">
                          <span className="text-gray-600 font-medium">ID:</span>
                          <span className="text-gray-900 text-right">
                            {driver.id}
                          </span>
                        </div>
                      </div>

                      {/* Action buttons */}
                      {hasPermission("driver:update") &&
                        hasPermission("driver:deactivate") &&
                        driver.id !== 19 && (
                          <div className="flex items-center justify-between sm:justify-end space-x-1 xs:space-x-2 mt-3 xs:mt-4 pt-2 xs:pt-3 sm:pt-4 border-t border-gray-100">
                            <div className="flex flex-col sm:flex-row sm:space-x-2 gap-1 w-full">
                              {hasPermission("driver:update") && (
                                <DriverModal
                                  trigger={
                                    <button
                                      className="border border-gray-100 inline-flex items-center justify-center px-2 xs:px-3 py-1.5 xs:py-2 text-xs sm:text-sm text-gray-600 bg-gray-50  hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors touch-manipulation"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                    >
                                      <svg
                                        className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 xs:mr-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                        />
                                      </svg>
                                      <span className="inline">Editar</span>
                                    </button>
                                  }
                                  driverToEdit={driver}
                                  refreshDrivers={async () => {
                                    fetchDrivers();
                                  }}
                                />
                              )}
                              {hasPermission("driver:deactivate") && (
                                <button
                                  className={`border border-gray-100 inline-flex items-center justify-center px-2 xs:px-3 py-1.5 xs:py-2 text-xs sm:text-sm rounded-md bg-gray-50 transition-colors touch-manipulation ${
                                    driver.active
                                      ? "text-red-600 hover:text-red-800 hover:bg-red-50"
                                      : "text-green-600 hover:text-green-800 hover:bg-green-50"
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateDriverStatus(
                                      String(driver.id),
                                      driver.active ?? false,
                                    );
                                  }}
                                >
                                  {driver.active ? (
                                    <>
                                      <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 xs:mr-1" />
                                      <span>Desactivar</span>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 xs:mr-1" />
                                      <span>Activar</span>
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  ))}
                </div>

                <Pagination
                  totalPages={totalPages}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
                {totalCount > 0 && (
                  <div className="text-sm text-gray-600 mt-2 px-6">
                    Mostrando página {currentPage} de {totalPages} ({totalCount}{" "}
                    conductores en total)
                  </div>
                )}
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
